<template>
    <div>
        <CompassTable :isCheckable="false" class="unit-settings-table">
            <CompassTableHead>
                <CompassTableHeadCell v-if="editMode"></CompassTableHeadCell>
                <CompassTableHeadCell>
                    {{ $t.__('common.labels.value') }}
                </CompassTableHeadCell>
                <CompassTableHeadCell v-if="showUnitCode">
                    {{ $t.__('common.labels.unit_code') }}
                </CompassTableHeadCell>

                <CompassTableHeadCell class="unit-settings-table__head--centered">
                    {{ $t.__('common.labels.default_unit') }}
                </CompassTableHeadCell>
                <CompassTableHeadCell v-if="editMode"></CompassTableHeadCell>
            </CompassTableHead>
            <SaasDraggable
                v-model="localUnits"
                dragClass="unit-settings-table__row"
                :isTable="true"
                :disabled="!editMode"
                handle=".unit-settings-table__drag-handle"
                @update:model-value="handleSortUpdate"
            >
                <CompassTableRow
                    v-for="unitItem in units"
                    :key="`${unitItem.id}-${unit}`"
                    class="unit-settings-table__row"
                >
                    <CompassTableBodyCell
                        v-if="editMode"
                        :width="64"
                        class="unit-settings-table__fixed-width-cell"
                    >
                        <div class="unit-settings-table__drag-handle">
                            <CompassIcon
                                icon="drag-and-drop"
                                :type="isEditable ? 'primary' : 'disabled'"
                            />
                        </div>
                    </CompassTableBodyCell>
                    <CompassTableBodyCell :width="220" maxWidth="220px">
                        <span v-if="!editMode" class="typographies-paragraph-medium-regular">
                            {{ unitItem.label }}
                        </span>
                        <CompassFormItem v-else label="" :error="getLabelError(unitItem)">
                            <CompassInput
                                v-model="unitItem.label"
                                :error="!!getLabelError(unitItem)"
                                @focus="unitFocused = unitItem.id"
                                @blur="getSuggestion(unitItem)"
                                @input="emptyCode(unitItem)"
                            />
                        </CompassFormItem>
                    </CompassTableBodyCell>
                    <CompassTableBodyCell v-if="showUnitCode">
                        <template v-if="!editMode">
                            <span
                                v-if="unitItem.parameters && unitItem.parameters.code"
                                class="typographies-paragraph-medium-regular"
                            >
                                {{ unitItem.parameters.code }}
                            </span>
                            <CompassTooltip
                                v-else
                                :content="$t.__('settings.units.unit_list.no_code.tooltip')"
                                :arrow="false"
                            >
                                <CompassIcon
                                    icon="warning-fill"
                                    class="unit-settings-table__warning-icon"
                                />
                            </CompassTooltip>
                        </template>
                        <div v-else class="unit-settings-table__suggestion-wrapper">
                            <CompassFormItem label="" :error="getCodeError(unitItem)">
                                <CompassInput
                                    v-model="unitItem.parameters.code"
                                    class="unit-settings-table__code-input"
                                    :error="!!getCodeError(unitItem)"
                                />
                            </CompassFormItem>
                            <template v-if="!unitItem.parameters.code">
                                <div
                                    v-if="showSuggestion(unitItem)"
                                    class="unit-settings-table__suggestion"
                                >
                                    <img
                                        src="/ressources/logged/img/icon/svg/wand.svg"
                                        height="20"
                                        width="20"
                                    />
                                    <span class="typographies-paragraph-medium-bold">
                                        {{ unitItem._embed.suggestion.code }}
                                    </span>
                                    <CompassButton
                                        :label="$t.__('common.actions.apply')"
                                        type="tertiary"
                                        size="small"
                                        @click="applySuggestion(unitItem.id)"
                                    />
                                </div>
                                <CompassLoader v-else-if="unitLoading === unitItem.id" />
                                <div
                                    v-else-if="unitFocused !== unitItem.id"
                                    class="unit-settings-table__suggestion--empty typographies-paragraph-medium-regular"
                                >
                                    {{ $t.__('settings.units.unit_list.no_suggestions') }}
                                </div>
                            </template>
                        </div>
                    </CompassTableBodyCell>
                    <CompassTableBodyCell
                        alignContent="center"
                        class="unit-settings-table__default-unit-cell"
                    >
                        <CompassTooltip
                            v-if="!editMode"
                            :content="$t.__('settings.units.unit_list.default_unit.tooltip')"
                            :disabled="!isNewUnit(unitItem.id)"
                            :arrow="false"
                        >
                            <CompassRadio
                                class="unit-settings-table__default-unit"
                                modelValue="Default unit"
                                :disabled="!canEditUnit || isNewUnit(unitItem.id)"
                                :checked="unit === unitItem.id"
                                @change="handleDefaultUnitUpdate(unitItem.id)"
                            />
                        </CompassTooltip>
                        <CompassIcon
                            v-if="editMode && unit === unitItem.id"
                            icon="success-circle-fill"
                            class="unit-settings-table__default-unit-check"
                        />
                    </CompassTableBodyCell>
                    <CompassTableBodyCell
                        v-if="editMode"
                        :width="64"
                        class="unit-settings-table__fixed-width-cell"
                    >
                        <CompassTooltip
                            :content="deleteButtonTooltipContent(unitItem.id)"
                            :disabled="isDeleteButtonEnabled(unitItem.id)"
                            :arrow="false"
                        >
                            <CompassIconButton
                                variant="destructive"
                                icon="bin"
                                :disabled="!isDeleteButtonEnabled(unitItem.id)"
                                @click="removeUnit(unitItem.id)"
                            />
                        </CompassTooltip>
                    </CompassTableBodyCell>
                </CompassTableRow>
            </SaasDraggable>
        </CompassTable>
        <AddUnitRow v-if="editMode" @addUnit="addUnit" />
    </div>
</template>
<script>
import SaasDraggable from 'vcomponents/common/SaasDraggable.vue';
import { getUnitCodeSuggestions } from 'api/settings/unitService';
import AddUnitRow from 'vcomponents/preferences/Units/AddUnitRow.vue';

export default {
    components: {
        SaasDraggable,
        AddUnitRow,
    },
    props: {
        units: {
            type: Array,
            required: true,
        },
        canEditUnit: {
            type: Boolean,
            required: true,
        },
        isEditable: {
            type: Boolean,
            required: true,
        },
        unit: {
            type: Number,
            required: true,
        },
        editMode: {
            type: Boolean,
            required: true,
        },
        showUnitCode: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['sort', 'update:unit', 'removeUnit', 'addUnit'],
    data() {
        return {
            localUnits: this.units,
            unitLoading: 0,
            unitFocused: 0,
        };
    },
    watch: {
        units(newValues) {
            this.localUnits = newValues;
        },
    },
    methods: {
        isNewUnit(unitId) {
            return typeof unitId === 'string' && unitId.startsWith('temp-');
        },
        isDeleteButtonEnabled(unitId) {
            return this.isEditable && this.unit !== unitId;
        },
        deleteButtonTooltipContent(unitId) {
            if (!this.editMode) {
                return this.$t.__('settings.units.unit_list.delete_button.tooltip');
            }

            return this.unit === unitId
                ? this.$t.__('settings.units.unit_list.delete_button.tooltip.default_unit')
                : this.$t.__('settings.units.unit_list.delete_button.tooltip');
        },
        handleDefaultUnitUpdate(unitId) {
            this.$emit('update:unit', unitId);
        },
        removeUnit(unitId) {
            this.$emit('removeUnit', unitId);
        },
        showSuggestion(unitItem) {
            return unitItem._embed.suggestion;
        },
        applySuggestion(unitId) {
            const unit = this.units.find((unit) => unit.id === unitId);
            if (unit) {
                unit.parameters.code = unit._embed.suggestion.code;
                delete unit._embed.suggestion;
            }
        },
        getSuggestion(unit) {
            if (!this.showUnitCode) {
                return;
            }
            if (unit.label && !unit.parameters.code) {
                this.unitLoading = unit.id;
                this.unitFocused = 0;
                getUnitCodeSuggestions([{ label: unit.label }])
                    .then(({ data }) => {
                        if (data.length && data[0].code) {
                            unit._embed.suggestion = data[0];
                        }
                    })
                    .finally(() => {
                        this.unitLoading = 0;
                    });
            }
        },
        addUnit() {
            this.$emit('addUnit');
            this.$nextTick(() => {
                const rows = this.$el.querySelectorAll('.unit-settings-table__row');
                const newUnitRow = rows[rows.length - 1].querySelector('.compass-input input');
                if (newUnitRow) {
                    newUnitRow.focus();
                }
            });
        },
        handleSortUpdate(newOrder) {
            this.$emit('sort', newOrder);
        },
        getLabelError(unit) {
            return this.getFieldError(unit, 'label');
        },
        getCodeError(unit) {
            return this.getFieldError(unit, 'code');
        },
        getFieldError(unit, field) {
            if (!unit._embed?.errors?.length) {
                return;
            }
            return unit._embed.errors.find((error) => error.field === field)?.message;
        },
        emptyCode(unitItem) {
            unitItem.parameters.code = '';
            unitItem._embed.suggestion = null;
        },
    },
};
</script>
<style lang="scss">
.unit-settings-table__row.sortable-drag {
    .compass-table__cell-container {
        min-height: 56px;
        padding: var(--spacing-2) var(--spacing-3);
    }
}
</style>
<style lang="scss" scoped>
.unit-settings-table {
    border-bottom: 1px solid var(--colors-common-border-lighter);

    &__head {
        &--centered {
            :deep() {
                .compass-table__cell-tooltip {
                    justify-content: center;
                }
            }
        }
    }

    :deep() {
        .compass-table__cell-container,
        .compass-form-item {
            width: 100%;
        }

        .compass-loader {
            flex-shrink: 0;
        }
    }

    &__default-unit {
        &-cell {
            white-space: nowrap;
            width: 1%;
        }

        &-check {
            color: var(--colors-common-icon-success);
        }

        :deep() {
            .compass-radio {
                gap: 0;

                &__label {
                    text-indent: -9999px;
                }
            }
        }
    }

    &__fixed-width-cell {
        white-space: nowrap;
        width: 1%;
    }

    &__drag-handle {
        align-items: center;
        display: flex;
        flex: 1;
        justify-content: center;
        min-height: 32px;
        width: 32px;
    }

    &__warning-icon {
        color: var(--colors-common-background-warning-default);
    }

    &__suggestion {
        align-items: center;
        color: var(--colors-common-text-subtle-default);
        display: flex;
        gap: var(--spacing-0);

        :deep(.compass-button) {
            margin: 0 0 0 var(--spacing-1);
        }

        &--empty {
            color: var(--colors-common-text-standard-caption);
        }

        &-wrapper {
            align-items: center;
            display: flex;
            gap: var(--spacing-3);
        }
    }

    &__code-input {
        width: 150px;
    }
}
</style>
